import { safeToNumber } from '@tock/coretypes';
import { stringify } from '@tock/query-string';
import AppConstants from 'constants/AppConstants';
import consumerProtos from 'utils/consumerProto';

const { ConsumerCalendarRequest } = consumerProtos;

export function fetchV2(
  ticketTypeAccessToken?: string,
  noCache = false,
  businessId?: number,
  excludedPurchaseIds?: number[]
): ApiAction {
  return {
    TockApi: {
      postProto: '/consumer/calendar/full/v2',
      type: AppConstants.CALENDAR_FETCH_V2,
      request: ConsumerCalendarRequest.fromObject({
        ...(ticketTypeAccessToken
          ? {
              ticketTypeAccessToken: [ticketTypeAccessToken],
            }
          : {}),
        ...(excludedPurchaseIds
          ? {
              excludePurchaseId: excludedPurchaseIds,
            }
          : {}),
      }),
      scope: businessId ? { businessId: safeToNumber(businessId) } : undefined,
      cacheOptions: {
        cacheControl: noCache ? 'no-cache' : null,
      },
      context: {
        ticketTypeAccessToken,
        businessId,
      },
      onError: {
        3022: '', // Bookings are disabled
        defaultToOriginal: true,
      },
    },
  };
}

export function fetchCachedCalendarV2(ticketTypeAccessToken?: string): ApiAction {
  return {
    TockApi: {
      postProto: '/consumer/calendar/full/v2',
      type: AppConstants.CALENDAR_FETCH_V2,
      request: ConsumerCalendarRequest.fromObject(
        ticketTypeAccessToken
          ? {
              ticketTypeAccessToken: [ticketTypeAccessToken],
            }
          : {}
      ),
      cacheOptions: {
        enableCache: true,
        maxAge: 60 * 1000,
      },
      context: {
        ticketTypeAccessToken,
      },
      onError: {
        3022: '', // Bookings are disabled
        defaultToOriginal: true,
      },
    },
  };
}

export function fetchDates({
  partySize,
  ticketTypeId,
  businessId,
  excludePurchaseId,
}: {
  partySize?: number;
  ticketTypeId?: number;
  businessId?: number;
  excludePurchaseId?: number;
}): ApiAction {
  return {
    TockApi: {
      getProto: `/consumer/calendar/date?${stringify({
        partySize,
        ticketTypeId,
        excludePurchaseId,
      })}`,
      type: AppConstants.BUSINESS_CALENDAR_DATES_FETCH,
      scope: businessId ? { businessId: safeToNumber(businessId) } : undefined,
    },
  };
}

export function fetchPreview(endDate: string): ApiAction {
  return {
    TockApi: {
      getProto: `/consumer/calendar/full/preview?${stringify({ endDate })}`,
      type: AppConstants.CALENDAR_FETCH,
    },
  };
}

export function fetchOfferings(
  ticketTypeAccessToken?: string,
  noCache = false,
  businessId?: number
): ApiAction {
  return {
    TockApi: {
      postProto: '/consumer/offerings',
      type: AppConstants.OFFERINGS_FETCH,
      request: ConsumerCalendarRequest.fromObject(
        ticketTypeAccessToken
          ? {
              ticketTypeAccessToken: [ticketTypeAccessToken],
            }
          : {}
      ),
      scope: businessId ? { businessId: safeToNumber(businessId) } : undefined,
      cacheOptions: {
        cacheControl: noCache ? 'no-cache' : null,
      },
      context: {
        ticketTypeAccessToken,
      },
      onError: {
        3022: '', // Bookings are disabled
        defaultToOriginal: true,
      },
    },
  };
}

export function fetchSingleOffering(
  ticketTypeId?: number,
  ticketTypeAccessToken?: string,
  noCache = false,
  businessId?: number
): ApiAction {
  return {
    TockApi: {
      postProto: '/consumer/offering',
      type: AppConstants.OFFERING_FETCH,
      request: ConsumerCalendarRequest.fromObject({
        ticketTypeId,
        ticketTypeAccessToken: ticketTypeAccessToken ? [ticketTypeAccessToken] : undefined,
      }),
      scope: businessId ? { businessId: safeToNumber(businessId) } : undefined,
      cacheOptions: {
        cacheControl: noCache ? 'no-cache' : null,
      },
      context: {
        ticketTypeId,
        ticketTypeAccessToken,
      },
      onError: {
        3022: '', // Bookings are disabled
        1001: '',
        defaultToOriginal: true,
      },
    },
  };
}

export function fetchCachedOfferings(
  ticketTypeAccessToken?: string,
  businessId?: number
): ApiAction {
  return {
    TockApi: {
      postProto: '/consumer/offerings',
      type: AppConstants.OFFERINGS_FETCH,
      request: ConsumerCalendarRequest.fromObject(
        ticketTypeAccessToken
          ? {
              ticketTypeAccessToken: [ticketTypeAccessToken],
            }
          : {}
      ),
      scope: businessId ? { businessId: safeToNumber(businessId) } : undefined,
      cacheOptions: {
        enableCache: true,
        maxAge: 60 * 1000,
      },
      context: {
        ticketTypeAccessToken,
      },
      onError: {
        3022: '', // Bookings are disabled
        defaultToOriginal: true,
      },
    },
  };
}

export function fetchOfferingsPreview(endDate: string): ApiAction {
  return {
    TockApi: {
      getProto: `/consumer/offerings/preview?${stringify({ endDate })}`,
      type: AppConstants.OFFERINGS_FETCH,
    },
  };
}

export function fetchSingleOfferingPreview(
  ticketTypeId?: number,
  ticketTypeAccessToken?: string,
  endDate?: string
): ApiAction {
  return {
    TockApi: {
      postProto: `/consumer/offering/preview?${stringify({ endDate })}`,
      type: AppConstants.OFFERING_FETCH,
      request: ConsumerCalendarRequest.fromObject({
        ticketTypeId,
        ticketTypeAccessToken: ticketTypeAccessToken ? [ticketTypeAccessToken] : undefined,
      }),
    },
  };
}

export function clearCalendarAndOfferings(): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_AND_OFFERINGS_CLEAR,
  };
}
